import { Tooltip } from '@mui/material'
import React from 'react'

export interface MonthlyBarSeries {
    name: string
    color: string
}

export interface MonthlyBarPoint {
    key: string
    label: string
    values: number[]
}

export interface MonthlyBarChartProps {
    series: MonthlyBarSeries[]
    points: MonthlyBarPoint[]
    formatValue: (value: number) => string
    formatAxis?: (value: number) => string
    height?: number
}

/** Barras agrupadas por mes (una por serie), con eje de referencia y detalle al pasar el cursor. */
const MonthlyBarChart = ({ series, points, formatValue, formatAxis = formatValue, height = 240 }: MonthlyBarChartProps) => {
    const max = Math.max(0, ...points.flatMap((point) => point.values))
    const ticks = max > 0 ? [max, max / 2, 0] : [0]

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
                {series.map((item) => (
                    <span key={item.name} className="inline-flex items-center gap-1.5 text-xs font-semibold text-k-ink-2">
                        <span className="h-3 w-3 rounded-[4px]" style={{ background: item.color }} />
                        {item.name}
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <div className="flex shrink-0 flex-col justify-between pb-6 text-right text-[11px] tabular-nums text-k-muted" style={{ height }}>
                    {ticks.map((tick) => <span key={tick}>{formatAxis(tick)}</span>)}
                </div>
                <div className="min-w-0 flex-1 overflow-x-auto">
                    <div className="relative flex min-w-max items-end gap-3 border-b border-l border-k-line px-2" style={{ height: height - 24 }}>
                        <span className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-k-line-soft" />
                        <span className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-k-line-soft" />
                        {points.map((point) => (
                            <Tooltip
                                key={point.key}
                                title={(
                                    <span className="flex flex-col gap-0.5 p-0.5">
                                        <span className="font-bold">{point.label}</span>
                                        {series.map((item, index) => <span key={item.name}>{item.name}: {formatValue(point.values[index] || 0)}</span>)}
                                    </span>
                                )}
                            >
                                <div className="flex h-full min-w-[34px] cursor-default items-end gap-1">
                                    {series.map((item, index) => (
                                        <span
                                            key={item.name}
                                            className="w-3.5 rounded-t-[4px] transition-[height]"
                                            style={{ height: `${max > 0 ? ((point.values[index] || 0) / max) * 100 : 0}%`, minHeight: point.values[index] ? 2 : 0, background: item.color }}
                                        />
                                    ))}
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                    <div className="flex min-w-max gap-3 px-2 pt-1.5">
                        {points.map((point) => (
                            <span key={point.key} className="min-w-[34px] text-center text-[11px] font-semibold capitalize text-k-muted" style={{ width: series.length * 14 + (series.length - 1) * 4 }}>
                                {point.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MonthlyBarChart
